import styled from '@emotion/styled';
import Image from 'next/image';
import type {
  InputHTMLAttributes,
  ReactNode,
  Ref,
} from 'react';
import { forwardRef } from 'react';
import type { FieldError } from 'react-hook-form';
import { colors } from '@/constants/colors';
import icNotice from '@/public/images/ic-notice.svg';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  error?: Partial<FieldError>;
  start?: ReactNode;
  end?: ReactNode;
  width?: string;
  inputHeight?: string;
  whiteBase?: boolean;
}

const InputBaseDiv = styled.div<InputProps>`
  width: ${({ width }) => width};
`;

const InputWrapDiv = styled.div`
  position: relative;
  width: 100%;
`;

const InputStyled = styled.input<InputProps>`
width: 100%;
height: ${({ inputHeight = '48px' }) => inputHeight};
padding: 12px 20px;
padding-right: ${({ end }) => end ? '50px' : ''};
padding-left: ${({ start }) => start ? '50px' : ''};
border-width: 1px;
border-style: solid;
border-color: ${({ whiteBase }) => whiteBase ? colors.brightGrey4 : colors.grey100};
background-color: ${({ whiteBase }) => whiteBase ? colors.whiteGrey3 : colors.white};
border-radius: 5px;
color: ${colors.textPrimary};
font-size: 14px;
font-weight: 400;
line-height: 150%;
::placeholder {
  color: ${colors.black300};
}
:disabled {
  border-color: ${colors.grey100};
  background-color: ${colors.black10};
  color: ${colors.black500};
}
:focus {
  border-color: ${colors.blue};
  outline: .5px solid ${colors.blue};
}
:-webkit-autofill {
  box-shadow: 0 0 0 1000px ${colors.white} inset;
}
`;

const StartEndWrapDiv = styled.div`
  position: absolute;
  top: 0;
  display: flex;
  width: 50px;
  height: 100%;
  align-items: center;
  justify-content: center;
`;

export const StartWrapDiv = styled(StartEndWrapDiv)`
  left: 0;
`;

export const EndWrapDiv = styled(StartEndWrapDiv)`
  right: 0;
`;

export const ErrorWrapDiv = styled.div`
  display: flex;
  align-items: center;
  margin-top: 8px;
  color: ${colors.danger};
  font-size: 12px;
  font-weight: 500;
  line-height: 24px;
  >span:last-of-type {
    margin-left: 4px;
  }
`;

const Input = (props: InputProps, forwardedRef: Ref<HTMLInputElement>) => {
  const { error, start, end, width, className } = props;

  return (
    <InputBaseDiv width={width} className={className}>
      <InputWrapDiv>
        {start && <StartWrapDiv>{start}</StartWrapDiv>}
        <InputStyled
          {...props}
          className=""
          ref={forwardedRef}
        />
        {end && <EndWrapDiv>{end}</EndWrapDiv>}
      </InputWrapDiv>
      {error && (
        <ErrorWrapDiv>
          <Image src={icNotice} alt="notice" />
          <span>{error.message}</span>
        </ErrorWrapDiv>
      )}
    </InputBaseDiv>
  );
};

export default forwardRef(Input);
